import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Clock, DollarSign } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { Card } from "@/pages/admin/panel/ui";
import { PublishChecklistCard } from "@/pages/admin/panel/PublishChecklist";
import { MarketingTab } from "@/pages/admin/panel/MarketingTab";
import { ExamFactsEditor } from "@/pages/admin/panel/ExamFacts";
import {
  BankAuditCard, BankCard, CourseImageCard, DebugToolsCard, EcoUpdateTab, EditCertTab, JobHistoryCard, PipelineCard, ReviewQueueTab,
  SalesCard, UdemySpecCard, UdemyVideosCard, VerificationLogTab, VideoLineCard, VideoTargetEditor,
} from "@/pages/admin/CertDetail";

export const STAGE_ORDER = [
  { key: "setup", name: "Setup" }, { key: "generate", name: "Generate" }, { key: "review", name: "Review" }, { key: "video", name: "Video" },
  { key: "udemy", name: "Udemy Pack" }, { key: "marketing", name: "Marketing" }, { key: "publish", name: "Publish" }, { key: "sell", name: "Sell" },
];

function Section({ title, children }) {
  return <div className="space-y-4" data-testid={`stage-view-${title.toLowerCase().replace(/ /g, "-")}`}><h2 className="text-base md:text-lg font-bold">{title}</h2>{children}</div>;
}

export function StageView({ stage, id, panel, hub, actions }) {
  const load = actions.reload;
  const spend = panel.spend;
  const st = hub.stages.find((s) => s.key === stage);
  const [params] = useSearchParams();
  switch (stage) {
    case "setup":
      return (
        <Section title="Stage 1 · Setup">
          {panel.bank.video_target_missing && (
            <div className="rounded-md border-2 border-amber-400 bg-amber-50 px-4 py-3 flex items-center gap-3" data-testid="video-target-missing-banner">
              <p className="text-sm font-semibold text-amber-900">This collection has NO video pool target (0). Set it before generation.</p>
              <VideoTargetEditor id={id} panel={panel} onSaved={load} />
            </div>
          )}
          {panel.bank.exam_facts_missing?.length > 0 && (
            <div className="rounded-md border-2 border-amber-400 bg-amber-50 px-4 py-3" data-testid="exam-facts-missing-banner">
              <p className="text-sm font-semibold text-amber-900 mb-2">Exam facts missing ({panel.bank.exam_facts_missing.join(", ")}) — the Udemy intro lecture, spec sheet, and course image need every fact.</p>
              <ExamFactsEditor id={id} cert={panel.cert} onSaved={load} />
            </div>
          )}
          <EditCertTab id={id} onChanged={load} />
          <EcoUpdateTab id={id} cert={panel.cert} onChanged={load} />
          <VerificationLogTab id={id} />
        </Section>
      );
    case "generate":
      return (
        <Section title="Stage 2 · Generate">
          {st?.status === "waiting" && st.button?.[2] === "start" && (
            <div className="rounded-xl border-2 border-sky-200 bg-white px-5 py-4 flex flex-wrap items-center gap-3" data-testid="start-generation-card">
              <p className="text-sm text-zinc-700">One click: the $2 test run goes first; if it passes, the full generation starts by itself and runs to the bank audit.</p>
              <button data-testid="start-generation-btn" onClick={actions.start} className="ml-auto rounded-md bg-sky-500 px-4 py-2 text-sm font-bold text-white hover:bg-sky-600">Start generation</button>
            </div>
          )}
          {hub.auto_advance?.blocked && <p className="rounded-md border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900" data-testid="auto-advance-blocked">{hub.auto_advance.blocked}</p>}
          <PipelineCard panel={panel} onFix={() => window.location.assign(`/admin/certs/${id}/stage/setup`)} />
          <JobHistoryCard id={panel.cert.id} openJob={params.get("job")} />
          <div className="grid sm:grid-cols-2 gap-4">
            <Card icon={DollarSign} title="Spend this cert" testid="panel-spend-card">
              <p className="text-[20px] font-bold">${spend.total.toFixed(2)}</p>
              <p className="text-xs text-zinc-400 mt-0.5" data-testid="spend-stages">
                Generation ${(spend.generation ?? 0).toFixed(2)} · Extraction ${(spend.extraction ?? 0).toFixed(2)} · Validation ${(spend.validation ?? 0).toFixed(2)} · TTS ${spend.tts.toFixed(2)} · <span data-testid="spend-marketing">Marketing copy ${(spend.marketing ?? 0).toFixed(2)}</span>
                {(spend.test_runs ?? 0) > 0 && <> · <span data-testid="spend-test-runs">Test runs ${spend.test_runs.toFixed(2)} (included above)</span></>}
              </p>
            </Card>
            <Card icon={Clock} title="Last run" testid="panel-lastrun-card">
              <p className="text-[20px] font-bold">{panel.last_run.day || "—"}</p>
              <p className="text-xs text-zinc-400 mt-0.5">{panel.last_run.completed_at ? `Completed ${new Date(panel.last_run.completed_at).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" })}` : "No completed runs yet"}</p>
            </Card>
          </div>
          <DebugToolsCard id={id} />
        </Section>
      );
    case "review":
      return (
        <Section title="Stage 3 · Review">
          <BankCard panel={panel} id={id} onChanged={load} />
          <BankAuditCard audit={panel.bank_audit} />
          <ReviewQueueTab id={id} />
        </Section>
      );
    case "video":
      return <Section title="Stage 4 · Video"><VideoLineCard panel={panel} id={id} refresh={load} /></Section>;
    case "udemy":
      return (
        <Section title="Stage 5 · Udemy Pack">
          <CourseImageCard id={id} image={panel.bank.course_image} onChanged={load} />
          <UdemyVideosCard id={id} bank={panel.bank} onChanged={load} />
          <UdemySpecCard id={id} spec={panel.bank.udemy_spec} onChanged={load} />
          {panel.cert.udemy_pack_downloaded_at && <p className="text-xs text-zinc-500" data-testid="udemy-downloaded">Pack downloaded {new Date(panel.cert.udemy_pack_downloaded_at).toLocaleString()}</p>}
        </Section>
      );
    case "marketing":
      return <Section title="Stage 6 · Marketing"><MarketingTab certId={id} onChanged={load} /></Section>;
    case "publish":
      return (
        <Section title="Stage 7 · Publish">
          <PublishChecklistCard checklist={panel.publish_checklist} status={panel.cert.status} />
          {panel.cert.stripe_price_missing && <p className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700" data-testid="price-missing-banner">Published without a price — checkout will fail.</p>}
          <p className="text-sm text-zinc-600" data-testid="public-links">Public: <a className="text-sky-600 hover:underline" href={`/${panel.cert.slug}`} target="_blank" rel="noreferrer">/{panel.cert.slug}</a> · <a className="text-sky-600 hover:underline" href={`/free?cert=${panel.cert.slug}`} target="_blank" rel="noreferrer">free-50</a></p>
        </Section>
      );
    case "sell":
      return <Section title="Stage 8 · Sell"><SalesRange id={id} /><SalesCard panel={panel} certId={id} onChanged={load} /></Section>;
    default:
      return <p className="text-sm text-red-600" data-testid="unknown-stage">Unknown stage "{stage}".</p>;
  }
}

function SalesRange({ id }) {
  const [range, setRange] = useState({ start: "", end: "" });
  const [data, setData] = useState(null);
  useEffect(() => {
    const p = {}; if (range.start) p.start = range.start; if (range.end) p.end = range.end;
    api.get(`/admin/certs/${id}/sales`, { params: p }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id, range]);
  return (
    <div className="bg-white border border-zinc-200 rounded-xl px-5 py-4" data-testid="sales-range-card">
      <div className="flex flex-wrap items-center gap-3 text-sm">
        <span className="font-bold">Date range</span>
        <input type="date" data-testid="sales-start" value={range.start} onChange={(e) => setRange({ ...range, start: e.target.value })} className="border border-zinc-300 rounded-md px-2 py-1" />
        <span className="text-zinc-400">to</span>
        <input type="date" data-testid="sales-end" value={range.end} onChange={(e) => setRange({ ...range, end: e.target.value })} className="border border-zinc-300 rounded-md px-2 py-1" />
        {(range.start || range.end) && <button className="text-sky-600 hover:underline" onClick={() => setRange({ start: "", end: "" })}>All time</button>}
      </div>
      {data && (
        <div className="mt-3 grid grid-cols-2 sm:grid-cols-5 gap-3 text-sm" data-testid="sales-range-numbers">
          {[["Visitors", data.visitors], ["Free-50 takers", data.free50], ["Paid users", data.paid], ["Revenue", `$${data.revenue.toFixed(2)}`], ["Refunds", data.refunds]].map(([k, v]) => (
            <div key={k}><p className="text-xs text-zinc-400">{k}</p><p className="text-lg font-bold">{v}</p></div>
          ))}
          <p className="col-span-full text-xs text-zinc-500">{data.funnel}{data.codes.length ? ` · codes: ${data.codes.map((c) => `${c.code} ×${c.uses}`).join(", ")}` : ""}</p>
        </div>
      )}
    </div>
  );
}
